/**
 * Client for the Ask panel — SSE over `fetch`, not `EventSource`.
 *
 * `EventSource` cannot set an `Authorization` header and only does GET, and this
 * endpoint needs both a bearer token and a POST body. Same constraint that made
 * `downloadReport` go through a blob rather than an `<a href>`.
 *
 * The event vocabulary is the harness's own (`api/routes/chat.py`), forwarded
 * unchanged by the proxy. Only the events this panel renders are typed; anything
 * else — `todos`, and whatever a future turn adds — falls through the union's
 * `type: string` arm and is ignored by the reducer rather than crashing it. A
 * closed union here would make adding a server-side event a breaking change to a
 * page that does not care about it.
 */
import { ApiError, type TokenGetter } from './leaderboard'

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export type AskEvent =
  /** A slice of the answer. `thinking` is reasoning, not the answer itself. */
  | { type: 'token'; content: string; thinking?: boolean }
  /** One of the analyst's five reads. This is the answer's provenance. */
  | { type: 'tool_call'; name: string; server?: string | null; args?: unknown }
  | { type: 'status'; message: string }
  /** The turn hit a limit; whatever arrived above it is partial. */
  | { type: 'bound'; message?: string }
  | { type: 'error'; message: string }
  | { type: 'done'; thread_id?: string; interrupted?: boolean }
  | { type: string }

export interface AskOptions {
  message: string
  /** Stable for the life of a conversation — it is the checkpointer's thread. */
  threadId: string
  getToken: TokenGetter
  signal?: AbortSignal
  onEvent: (event: AskEvent) => void
}

/**
 * Put one question to the analyst and drive `onEvent` until the stream ends.
 *
 * Resolves when the stream closes. It does **not** resolve on `done`: the server
 * always sends one, but a caller that stopped reading at it would leave the
 * connection open, and `done` is a payload rather than a framing signal.
 *
 * Throws `ApiError` only for a failed *handshake* — a 401 the caller turns into
 * the sign-in gate, exactly like every other call in this UI. Once the stream is
 * open, failures arrive as `error` events instead, because by then part of the
 * answer may already be on screen and throwing would discard it.
 */
export async function streamAsk(opts: AskOptions): Promise<void> {
  const token = await opts.getToken()
  const resp = await fetch(`${BASE}/api/ask/stream`, {
    method: 'POST',
    signal: opts.signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message: opts.message, thread_id: opts.threadId }),
  })

  if (!resp.ok || !resp.body) {
    let detail = resp.statusText
    try {
      const body = await resp.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* non-JSON body — keep the status text */
    }
    throw new ApiError(resp.status, detail || 'Ask stream failed')
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const flush = (line: string) => {
    // `: keepalive` comments hold no data and are how the ALB's 300 s idle
    // timeout is kept off a long answer.
    if (!line.startsWith('data:')) return
    const raw = line.slice(5).trim()
    if (!raw) return
    try {
      opts.onEvent(JSON.parse(raw) as AskEvent)
    } catch {
      /* a chunk that is not JSON is not an answer — drop it */
    }
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    // The last element is whatever came after the final newline: either '' or a
    // half-received line. Keeping it is what stops a token being split in two.
    buffer = lines.pop() ?? ''
    for (const line of lines) flush(line)
  }
  flush(buffer)
}
